import { fetchCMSData } from "@clients/contentfulClient";
import { ExtendedFileListResultType, FilesPaginationContainer } from "@components/FileList";
import { FileListBlockType_fileListBlock } from "@components/FileListBlock/queries/contentfulTypes/FileListBlockType";

import { FileBlockDataType } from "types/FileBlockDataType";
import { UrlStructure } from "types/UrlStructure";

import { FileListResultType_fileBlockCollection_items } from "../components/FileList/queries/contentfulTypes/FileListResultType";
import { GET_FILELIST_RESULTS } from "../components/FileList/queries/getFileListBlockResult.query";
import { getDateByYearAndMonth } from "./dateHelpers";
import { getFileImageUrls } from "./imageHelpers/fileThumbnailImageHelper";
import { getUrlByLink } from "./pageHelpers/getUrlByLink";
import { getFileSize } from "./string";
import { replaceImageVaultBasePath } from "./urlResolver";

export const GetAllFileListItems = async (
  fileListBlock: FileListBlockType_fileListBlock,
  urlStructure: UrlStructure,
  paginationPage: number,
  preview: boolean,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<FilesPaginationContainer> => {
  let dynamicFileList = new Array<any>();
  let totalResultFiles;
  //Hämtar dynamiska filer
  if (fileListBlock?.fileListType !== "Bara manuellt utvalda filer") {
    const data = await GetFileListQuery(
      fileListBlock?.categories,
      fileListBlock?.dynamicFilesFilter,
      preview,
    );

    dynamicFileList = data;

    if (fileListBlock?.yearFilter?.length > 0) {
      const yearFilter = fileListBlock?.yearFilter;

      dynamicFileList = dynamicFileList?.filter(
        (file: FileListResultType_fileBlockCollection_items) =>
          yearFilter?.some(
            (filter: any) =>
              filter ===
              file?.metaData?.Categories?.filter((category: any) => category?.parent === "Årtal")[0]
                ?.name,
          ),
      );
    }

    if (fileListBlock?.typeOfMediaFilter?.length > 0) {
      const mediaFilter = fileListBlock?.typeOfMediaFilter;

      dynamicFileList = dynamicFileList?.filter(
        (file: FileListResultType_fileBlockCollection_items) =>
          mediaFilter?.some(
            (filter: any) =>
              filter ===
              file?.metaData?.Categories?.filter(
                (category: any) => category?.parent === "Media" && category?.name === filter,
              )[0]?.name,
          ),
      );
    }
  }

  let orderedDynamicFiles = OrderListItems(dynamicFileList, fileListBlock);

  //Hämtar manuellt utvalda filer och returnerar dessa tillsammans med den dynamiska fil-listan.
  if (fileListBlock?.fileListType !== "Bara automatiskt utvalda filer") {
    const manualFiles = fileListBlock?.manualPostsCollection
      ?.items as Array<FileListResultType_fileBlockCollection_items>;

    const uniqueManualFiles = [
      ...new Map(manualFiles?.map((item) => [item?.sys?.id, item])).values(),
    ];

    if (orderedDynamicFiles?.length) {
      orderedDynamicFiles = orderedDynamicFiles?.filter(
        (file: FileListResultType_fileBlockCollection_items) =>
          !uniqueManualFiles?.some(
            (manualFile: FileListResultType_fileBlockCollection_items) =>
              manualFile?.sys?.id === file?.sys?.id,
          ),
      );
    }

    totalResultFiles = [].concat(
      uniqueManualFiles,
      orderedDynamicFiles,
    ) as Array<FileListResultType_fileBlockCollection_items>;
  } else {
    totalResultFiles = orderedDynamicFiles;
  }

  totalResultFiles = totalResultFiles.filter((file) => !!file);

  for (const item of totalResultFiles) {
    if (item?.relatedInfoLink) {
      item.relatedInfoLink.url = getUrlByLink(item.relatedInfoLink, urlStructure);
    }
    const file = item?.imageVaultFile && item?.imageVaultFile[0];
    const thumbnailImage = item?.ivThumbnail && item?.ivThumbnail[0];
    const metaData = item?.metaData;
    const urls = getFileImageUrls(thumbnailImage, file);
    item.fileData = {
      fileSize: getFileSize(metaData?.fileSize),
      fileUrl: file?.mediaUrl ? replaceImageVaultBasePath(file?.mediaUrl) : null,
      thumbNailAlt: thumbnailImage?.altText || file?.altText || null,
      transformedFileSize: metaData?.fileSize
        ? metaData?.fileSize.replace("MB", "Megabyte").replace("KB", "Kilobyte")
        : null,
      urls: urls,
      hasImage: Object.keys(urls).length ? true : false,
      hasExtraThumbnail: thumbnailImage?.mediaUrl ? true : false,
    } as FileBlockDataType;
    item.ivThumbnail = null;
    item.imageVaultFile = null;
  }

  let resultItems = [].concat(
    totalResultFiles?.filter((file: ExtendedFileListResultType) => file?.fileData),
  );
  if (!fileListBlock.pagination && fileListBlock?.numberOfItems < resultItems?.length) {
    resultItems = resultItems?.slice(0, fileListBlock?.numberOfItems);
  } else {
    if (fileListBlock?.numberOfItems < resultItems?.length)
      resultItems = resultItems?.slice(
        fileListBlock?.numberOfItems * (paginationPage - 1),
        fileListBlock?.numberOfItems * (paginationPage - 1) + fileListBlock?.numberOfItems,
      );
  }

  const paginationContainer = {
    listResult: resultItems,
    totalItems: totalResultFiles?.length,
  } as FilesPaginationContainer;
  return paginationContainer;
};

export const OrderListItems = (
  dynamicFileList: FileListResultType_fileBlockCollection_items[],
  fileList: FileListBlockType_fileListBlock,
): Array<FileListResultType_fileBlockCollection_items> => {
  if (dynamicFileList?.length < 1) return dynamicFileList;

  const headingType = fileList?.headingType;
  const isheadingOnWeb = headingType === "Rubrik på webb";
  const year = "Årtal";
  const month = "Månad eller kvartal";

  //list should order list 1. files with years with/without month, 2. files with month only, 3. files with no year or month.
  const itemsWithYear = dynamicFileList?.filter(
    (item: FileListResultType_fileBlockCollection_items) =>
      item?.metaData?.Categories?.some((category: any) => category?.parent === year),
  );
  const itemsWithMonthOnly = dynamicFileList?.filter(
    (item: FileListResultType_fileBlockCollection_items) =>
      !item?.metaData?.Categories?.some((category: any) => category?.parent === year) &&
      item?.metaData?.Categories?.some((category: any) => category?.parent === month),
  );
  const itemsWithoutCategories = dynamicFileList?.filter(
    (item: FileListResultType_fileBlockCollection_items) =>
      !item?.metaData?.Categories?.some((category: any) => category?.parent === year) &&
      !item?.metaData?.Categories?.some((category: any) => category?.parent === month),
  );

  switch (fileList?.sortBy) {
    case "Senaste år och månad överst":
      return itemsWithYear
        ?.sort(
          (a, b) =>
            getDateByYearAndMonth(
              b?.metaData?.Categories?.filter((category: any) => category?.parent === year)[0]
                ?.name,
              b?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                ?.name
                ? b?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                    ?.name
                : "Januari",
            )?.getTime() -
            getDateByYearAndMonth(
              a?.metaData?.Categories?.filter((category: any) => category?.parent === year)[0]
                ?.name,
              a?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                ?.name
                ? a?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                    ?.name
                : "Januari",
            )?.getTime(),
        )
        ?.concat(itemsWithMonthOnly)
        ?.concat(itemsWithoutCategories);

    case "Tidigaste år och månad överst":
      return itemsWithYear
        ?.sort(
          (a, b) =>
            getDateByYearAndMonth(
              a?.metaData?.Categories?.filter((category: any) => category?.parent === year)[0]
                ?.name,
              a?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                ?.name
                ? a?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                    ?.name
                : "December",
            )?.getTime() -
            getDateByYearAndMonth(
              b?.metaData?.Categories?.filter((category: any) => category?.parent === year)[0]
                ?.name,
              b?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                ?.name
                ? b?.metaData?.Categories?.filter((category: any) => category?.parent === month)[0]
                    ?.name
                : "December",
            )?.getTime(),
        )
        ?.concat(itemsWithMonthOnly)
        ?.concat(itemsWithoutCategories);

    //default is alphabetic order A-Ö
    default:
      return dynamicFileList?.sort((a, b) =>
        (isheadingOnWeb ? a?.metaData?.headingWeb : a?.metaData?.headingFileListWeb)?.localeCompare(
          isheadingOnWeb ? b?.metaData?.headingWeb : b?.metaData?.headingFileListWeb,
        ),
      );
  }
};

export const GetFileListQuery = async (
  tags?: string[],
  dynamicFilesFilter?: string,
  preview?: boolean,
): Promise<Array<FileListResultType_fileBlockCollection_items>> => {
  const files: Array<FileListResultType_fileBlockCollection_items> = [];

  const filter =
    dynamicFilesFilter === "Det räcker att en av de markerade kategorierna matchar med filen"
      ? "id_contains_some"
      : "id_contains_all";

  const getAllFiles = async (
    limit,
    skip,
  ): Promise<Array<FileListResultType_fileBlockCollection_items>> => {
    const response = await fetchCMSData(
      GET_FILELIST_RESULTS(limit, skip, tags, filter, preview),
      preview,
    );
    const { data } = await response;

    for (const i of data?.fileBlockCollection?.items) {
      files.push(i);
    }
    if (data?.fileBlockCollection?.total > files?.length) {
      return await getAllFiles(limit, files?.length);
    }
    return files;
  };

  const allPages = await getAllFiles(200, 0);
  const listResult = allPages as Array<FileListResultType_fileBlockCollection_items>;

  return listResult;
};
